"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  LayoutDashboard,
  FolderKanban,
  ScrollText,
  CreditCard,
  Settings,
  FolderTree,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

// 각 메뉴가 요구하는 권한(모듈화). 사용자 권한에 없으면 메뉴 숨김.
type Leaf = { href: string; label: string; exact?: boolean; perm?: string };
type Group = { label: string; icon: LucideIcon; children: Leaf[] };
type Item = (Leaf & { icon: LucideIcon }) | Group;

const items: Item[] = [
  { href: "/discovery", label: "비즈니스·콘텐츠 발굴", icon: FolderTree, perm: "projects.read" },
  { href: "/", label: "매출 작업실", icon: LayoutDashboard, exact: true, perm: "projects.read" },
  { href: "/operations", label: "운영 점검", icon: LayoutDashboard, perm: "projects.read" },
  { href: "/projects", label: "아이템·개발", icon: FolderKanban, perm: "projects.read" },
  {
    label: "기초자료",
    icon: CreditCard,
    children: [
      { href: "/basics", label: "요약", exact: true, perm: "basics.read" },
      { href: "/basics/subscriptions", label: "구독·결제", perm: "basics.read" },
      { href: "/basics/accounts", label: "계정", perm: "basics.read" },
      { href: "/basics/infra", label: "서버·도메인", perm: "basics.read" },
    ],
  },
  { href: "/resources", label: "리소스", icon: FolderTree, perm: "resources.read" },
  { href: "/history", label: "히스토리", icon: ScrollText, perm: "history.read" },
  {
    label: "설정",
    icon: Settings,
    children: [
      { href: "/settings", label: "환경설정", exact: true, perm: "settings.read" },
      { href: "/settings/ai", label: "AI 실행·비용", perm: "settings.read" },
      { href: "/settings/users", label: "사용자관리", perm: "users.manage" },
      { href: "/settings/orgs", label: "조직", perm: "users.manage" },
      { href: "/settings/scenarios", label: "시나리오 템플릿", perm: "scenarios.write" },
    ],
  },
];

function allowed(perm: string | undefined, perms: readonly string[]): boolean {
  return !perm || perms.includes(perm);
}

// 권한 필터: 항목/그룹 children 을 사용자 권한으로 걸러냄.
function visibleItems(perms: readonly string[]): Item[] {
  return items
    .map((it) => {
      if ("children" in it) {
        const children = it.children.filter((c) => allowed(c.perm, perms));
        return children.length ? { ...it, children } : null;
      }
      return allowed(it.perm, perms) ? it : null;
    })
    .filter((x): x is Item => x !== null);
}

function isActive(pathname: string, href: string, exact?: boolean): boolean {
  return exact ? pathname === href : pathname === href || pathname.startsWith(href + "/");
}

export function SidebarNav({ onNavigate, permissions = [] }: { onNavigate?: () => void; permissions?: string[] }) {
  const pathname = usePathname();
  const navItems = visibleItems(permissions);
  return (
    <nav className="flex flex-col gap-0.5">
      {navItems.map((item) =>
        "children" in item ? (
          <div key={item.label} className="mt-1">
            <div className="flex items-center gap-2.5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
              <item.icon className="h-4 w-4 shrink-0" />
              {item.label}
            </div>
            <div className="ml-3 flex flex-col gap-0.5 border-l border-neutral-200 pl-2">
              {item.children.map((c) => {
                const active = isActive(pathname, c.href, c.exact);
                return (
                  <Link
                    key={c.href}
                    href={c.href}
                    onClick={onNavigate}
                    className={cn(
                      "rounded-md px-2.5 py-1.5 text-sm transition-colors",
                      active ? "bg-neutral-900 font-medium text-white" : "text-neutral-600 hover:bg-neutral-200/60 hover:text-neutral-900",
                    )}
                  >
                    {c.label}
                  </Link>
                );
              })}
            </div>
          </div>
        ) : (
          <Link
            key={item.href}
            href={item.href}
            onClick={onNavigate}
            className={cn(
              "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              isActive(pathname, item.href, item.exact)
                ? "bg-neutral-900 text-white"
                : "text-neutral-600 hover:bg-neutral-200/60 hover:text-neutral-900",
            )}
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {item.label}
          </Link>
        ),
      )}
    </nav>
  );
}
